/**
 * Business service — typed wrappers around /businesses/* endpoints.
 *
 * Why a thin service layer instead of calling api.get directly in
 * components: keeps response shape extraction (response.data.data)
 * and type narrowing in one place. Consumers (hooks, components) get
 * pre-typed return values.
 */
import api from "./api";
import type { Business, PortfolioToday } from "@/types/business.types";

interface ApiEnvelope<T> {
  success: boolean;
  data: T;
}

const businessService = {
  /**
   * Fetch the businesses the current user can access.
   *
   * - Tenant-wide roles (tenant_owner / super_admin): all active
   *   businesses in their company.
   * - Other roles: just their own (single-element array).
   *
   * Called on every login to drive portfolio-vs-auto-select routing.
   */
  async getMyBusinesses(): Promise<Business[]> {
    const response = await api.get<ApiEnvelope<Business[]>>("/businesses/mine");
    return response.data.data;
  },

  /**
   * Fetch today's portfolio overview. Only callable by tenaant_owner
   * and super_admin (the backend enforces; calling from other roles
   * returns 400 from roleMiddleware).
   */
  async getPortfolioToday(): Promise<PortfolioToday> {
    const response = await api.get<ApiEnvelope<PortfolioToday>>(
      "/businesses/portfolio/today",
    );
    return response.data.data;
  },
};

export default businessService;
